import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Star, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "@/lib/api";
import Scoreboard from "@/components/Scoreboard";
import ArticleCard from "@/components/ArticleCard";
import Reveal from "@/components/Reveal";
import FanZone from "@/components/FanZone";
import ShareButtons from "@/components/ShareButtons";
import PitchView from "@/components/PitchView";

const parseContent = (content) => {
  const lines = (content || "").split("\n");
  const nodes = [];
  let list = [];
  const flush = () => {
    if (list.length) {
      nodes.push(
        <ul key={`ul-${nodes.length}`} className="my-5 space-y-2">
          {list.map((li, i) => (
            <li key={i} className="flex items-start gap-3 text-slate-300">
              <span className="mt-2 h-2 w-2 shrink-0 rotate-45 bg-crimson" />
              {li}
            </li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (line.startsWith("- ")) {
      list.push(line.slice(2));
      return;
    }
    flush();
    if (!line) return;
    if (line.startsWith("## ")) {
      nodes.push(
        <h2 key={i} className="mt-10 font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith("> ")) {
      nodes.push(
        <blockquote key={i} className="my-7 border-l-4 border-crimson bg-ink2 py-4 pl-6 pr-4 font-display text-xl font-bold uppercase leading-snug text-white">
          {line.slice(2)}
        </blockquote>
      );
    } else {
      nodes.push(
        <p key={i} className="my-5 leading-relaxed text-slate-300">
          {line}
        </p>
      );
    }
  });
  flush();
  return nodes;
};

const RatingBar = ({ player, rating }) => (
  <div className="flex items-center gap-4" data-testid={`player-rating-${player.toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="w-36 truncate font-display text-base font-bold uppercase text-white sm:w-48">{player}</p>
    <div className="h-2 flex-1 overflow-hidden rounded-full bg-ink3">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${(rating / 10) * 100}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className={`h-full rounded-full ${rating >= 8 ? "bg-glow" : rating >= 6 ? "bg-crimson" : "bg-slate-500"}`}
      />
    </div>
    <span className={`w-10 text-right font-mono-score text-sm font-bold ${rating >= 8 ? "text-glow" : "text-white"}`}>
      {rating.toFixed(1)}
    </span>
  </div>
);

const TABS = ["resumen", "alineacion", "calificaciones", "claves"];

const ArticleDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");
  const [tab, setTab] = useState("resumen");

  useEffect(() => {
    setTab("resumen");
    setStatus("loading");
    API.get(`/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        setStatus("ok");
        return API.get("/articles", {
          params: { category: data.category, limit: 4 },
        });
      })
      .then(({ data }) => setRelated((data || []).filter((a) => a.slug !== slug).slice(0, 3)))
      .catch(() => setStatus("error"));
  }, [slug]);

  const readMin = useMemo(
    () => Math.max(2, Math.round((article?.content || "").split(/\s+/).length / 210)),
    [article]
  );

  if (status === "loading")
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink pt-32">
        <p className="animate-pulse font-display text-2xl uppercase tracking-widest text-slate-500">
          Cargando...
        </p>
      </div>
    );

  if (status === "error" || !article)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink pt-32 text-center">
        <AlertCircle className="h-10 w-10 text-crimson" />
        <p className="font-display text-3xl font-bold uppercase text-white">Contenido no encontrado</p>
        <Link to="/" className="flex items-center gap-2 text-crimson hover:underline">
          <ArrowLeft className="h-4 w-4" /> Volver al inicio
        </Link>
      </div>
    );

  const isCronica = article.category === "cronica";

  return (
    <div className="bg-ink pt-24">
      {/* Portada */}
      <div className="relative h-[45vh] min-h-[320px] overflow-hidden">
        <img src={article.cover_image} alt={article.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/20" />
      </div>

      <article className="mx-auto max-w-3xl px-5 md:px-8">
        <Reveal>
          <div className="-mt-24 relative">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-crimson px-3 py-1 font-mono-score text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                {isCronica ? "Crónica" : "Artículo"}
              </span>
              <span className="font-mono-score text-[11px] uppercase tracking-widest text-slate-400">
                @ENOLFC · {article.created_at?.slice(0, 10)} · {readMin} min de lectura
              </span>
            </div>
            <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-6xl">
              {article.title}
            </h1>
            <p className="mt-5 border-l-2 border-crimson pl-5 text-lg leading-relaxed text-slate-300">
              {article.excerpt}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {(article.tags || []).map((t) => (
                <span key={t} className="rounded-full border border-edge px-3 py-1 font-mono-score text-[10px] uppercase tracking-widest text-slate-400">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        {isCronica && (
          <Reveal delay={0.05} className="mt-6">
            <ShareButtons title={article.title} />
          </Reveal>
        )}

        {isCronica && (
          <Reveal delay={0.1} className="mt-10">
            <Scoreboard match={article} size="lg" />
          </Reveal>
        )}

        {isCronica && (
          <div className="mt-8 flex gap-2" data-testid="cronica-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`cronica-tab-${t}`}
                onClick={() => setTab(t)}
                className={`rounded-full px-5 py-2 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
                  tab === t ? "bg-crimson text-white" : "border border-edge text-slate-400 hover:text-white"
                }`}
              >
                {t === "resumen" ? "Resumen" : t === "alineacion" ? "Alineación" : t === "calificaciones" ? "Calificaciones" : "Claves tácticas"}
              </button>
            ))}
          </div>
        )}

        <div className="mt-10 pb-4" data-testid="article-content">
          {(!isCronica || tab === "resumen") && parseContent(article.content)}

          {isCronica && tab === "calificaciones" && (
            <div className="space-y-4">
              {(article.player_ratings || []).length > 0 ? (
                article.player_ratings.map((r, i) => <RatingBar key={i} {...r} />)
              ) : (
                <p className="text-slate-500">El autor aún no ha puntuado a los jugadores de este partido.</p>
              )}
            </div>
          )}

          {isCronica && tab === "alineacion" && (
            <div data-testid="lineup-list">
              <PitchView lineup={article.lineup || []} formation={article.formation} />
              <p className="mb-3 font-mono-score text-[11px] font-semibold uppercase tracking-[0.25em] text-glow">
                Once inicial
              </p>
              <div className="space-y-2.5">
                {(article.lineup || []).length > 0 ? (
                  article.lineup.map((p, i) => (
                    <div key={i} className="flex items-center gap-4 rounded-xl border border-edge bg-ink2 px-4 py-3" data-testid={`lineup-player-${i}`}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-crimson font-mono-score text-sm font-bold text-white">
                        {i + 1}
                      </span>
                      <p className="font-display text-lg font-bold uppercase text-white">{p}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500">El autor aún no ha publicado el once inicial.</p>
                )}
              </div>

              {(article.bench || []).length > 0 && (
                <>
                  <p className="mb-3 mt-8 font-mono-score text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Banquillo
                  </p>
                  <div className="space-y-2.5" data-testid="bench-list">
                    {article.bench.map((p, i) => (
                      <div key={i} className="flex items-center gap-4 rounded-xl border border-edge bg-ink px-4 py-2.5" data-testid={`bench-player-${i}`}>
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-ink3 font-mono-score text-xs font-bold text-slate-400">
                          {12 + i}
                        </span>
                        <p className="font-display text-base font-bold uppercase text-slate-200">{p}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {(article.substitutions || []).length > 0 && (
                <>
                  <p className="mb-3 mt-8 font-mono-score text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Sustituciones
                  </p>
                  <div className="space-y-2" data-testid="substitutions-list">
                    {article.substitutions.map((s, i) => (
                      <p key={i} className="flex items-center gap-3 font-mono-score text-sm text-slate-300" data-testid={`substitution-${i}`}>
                        <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-crimson" /> {s}
                      </p>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {isCronica && tab === "claves" && (
            <div className="space-y-4">
              {(article.tactical_keys || []).length > 0 ? (
                article.tactical_keys.map((k, i) => (
                  <div key={i} className="rounded-2xl border border-edge bg-ink2 p-6">
                    <p className="flex items-center gap-3 font-display text-xl font-bold uppercase text-white">
                      <span className="font-mono-score text-crimson">{String(i + 1).padStart(2, "0")}</span>
                      {k.title}
                    </p>
                    <p className="mt-2 text-slate-300">{k.text}</p>
                  </div>
                ))
              ) : (
                <p className="text-slate-500">Sin claves tácticas para este partido.</p>
              )}
            </div>
          )}
        </div>

        {isCronica && (
          <div className="mt-6 pb-8">
            <FanZone slug={article.slug} />
          </div>
        )}

        <div className="flex justify-center border-t border-edge py-10">
          <Link to={isCronica ? "/cronicas" : "/articulos"} className="flex items-center gap-2 font-display text-base font-bold uppercase tracking-wider text-crimson hover:underline">
            <ArrowLeft className="h-4 w-4" /> Ver más {isCronica ? "crónicas" : "artículos"}
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-white/10 bg-ink2 py-16">
          <div className="mx-auto max-w-7xl px-5 md:px-12">
            <Reveal>
              <h2 className="mb-8 font-display text-2xl font-extrabold uppercase text-white sm:text-4xl">
                Seguir leyendo
              </h2>
            </Reveal>
            <div className="grid gap-5 md:grid-cols-3">
              {related.map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.08}>
                  <ArticleCard article={a} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ArticleDetail;
